import React, { useEffect, useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import styles from "./Resumen.module.scss";
import { Mail, Phone, Globe, Instagram, Linkedin, Facebook, Twitter, MapPin, FileText, ExternalLink } from "lucide-react";
import {
  formatUserCvUpdatedAt,
  parseUserCvFromProfile,
} from "../../../../utils/userCv.ts";
import MisSkills from "../MisSkills/MisSkills.js";
import RegisteredUserProfileLoader from "../../../RegisteredUsers/RegisteredUsersSkeleton/RegisteredUserProfileLoader.js";
import { normalizeUserCommerces } from "../../../../utils/userCommerces.ts";
import { buildProfileUrl, getUserDisplayName } from "../../../../utils/navigationHelpers.ts";
import {
  getCommerce,
  getCommerceCatalogMaterials,
  unwrapCommerceCatalogMaterialsPage,
} from "../../../../services/commerceService.ts";
import { getResumenAccountKind, type ResumenAccountKind } from "../../../../utils/jobboard/jobboardProfileGate.ts";

const MIN_LOADING_TIME = 400;
const MAX_LOADING_TIME = 8000;

type CatalogInsightState = {
  status: "idle" | "loading" | "ready" | "error";
  itemTotal: number | null;
  lastShopUpdateLabel: string | null;
};

function parseCommerceDetailUpdatedMs(data: unknown): number | null {
  const root = data as Record<string, unknown> | null | undefined;
  if (root == null) return null;
  const inner = (root.data ?? root) as Record<string, unknown>;
  const keys = ["updated_at", "updatedAt", "catalog_updated_at", "modified_at"];
  for (const k of keys) {
    const v = inner[k];
    if (typeof v === "string" && v.trim().length > 0) {
      const t = Date.parse(v);
      if (Number.isFinite(t)) return t;
    }
  }
  return null;
}

function formatProfileFieldDate(value: unknown): string | null {
  if (value == null || value === "") return null;
  if (typeof value === "string" && value.trim().length > 0) {
    const t = Date.parse(value);
    if (Number.isFinite(t)) {
      return new Date(t).toLocaleDateString("es-AR");
    }
  }
  return null;
}

function formatOptionalYesNo(value: unknown): string {
  if (value === true) return "Sí";
  if (value === false) return "No";
  return "No especificado";
}

interface CategoryConnectItem {
  name?: string;
}

interface JobBoardZone {
  allCountry?: boolean;
  jurisdiccion?: { name?: string };
  city?: string;
}

interface ProfileData {
  id?: string | number;
  username?: string;
  description?: string | null;
  type?: string;
  linkedin?: string;
  instagram?: string;
  facebook?: string;
  twitter?: string;
  phone?: string;
  jobBoardZones?: JobBoardZone[];
  categoryConnect?: CategoryConnectItem[];
  commerces?: { id?: number; name?: string }[];
  rols?: { name?: string }[];
  unemployment_fund?: boolean;
  current_job?: boolean;
  employment_end_date?: string | null;
  created_at?: string;
  updated_at?: string;
}

function resumenMicrocopy(kind: ResumenAccountKind, myPerson: boolean) {
  const companyClient =
    kind === "client_company" || kind === "other_company";

  const descriptionTitle = (() => {
    if (kind === "jobboard_worker") return "Presentación";
    if (companyClient) return "Descripción de la empresa";
    return "Descripción profesional";
  })();

  const descriptionLead = (() => {
    if (myPerson) {
      if (kind === "jobboard_worker") {
        return "Lo que figura acá es lo que otros ven en tu perfil de trabajador/a.";
      }
      if (companyClient) {
        return "Texto que mostrás como empresa en tu ficha pública.";
      }
      return "Texto que mostrás en tu ficha pública de Edify.";
    }
    if (kind === "jobboard_worker") {
      return "Así se presenta para oportunidades laborales y mensajes en la red.";
    }
    if (companyClient) {
      return "Resumen que la empresa eligió mostrar en Edify a clientes y proveedores.";
    }
    return "Enfoque y trayectoria que esta persona quiere destacar en su perfil público.";
  })();

  const skillsLead = (() => {
    if (myPerson) {
      if (kind === "jobboard_worker") {
        return "Especialidades y oficios que mostrás en tu perfil de trabajador/a.";
      }
      if (companyClient) {
        return "Rubros y actividades declaradas para esta cuenta.";
      }
      return "Rubros y actividades que declarás en tu cuenta de Edify.";
    }
    if (kind === "jobboard_worker") {
      return "Oficios y rubros en los que puede ayudarte u ofrecer servicios.";
    }
    if (companyClient) {
      return "Actividades en las que la empresa declara experiencia u oferta.";
    }
    return "Disciplinas en las que trabaja o cotiza, según lo declarado en Edify.";
  })();

  const zonesLead = (() => {
    if (myPerson) {
      if (kind === "jobboard_worker") {
        return "Zonas en las que buscás u ofrecés trabajo en Edify.";
      }
      if (companyClient) {
        return "Provincias y localidades donde trabajan o entregan.";
      }
      return "Provincias y localidades donde trabajás o entregás.";
    }
    if (kind === "jobboard_worker") {
      return "Zona de búsqueda u oferta laboral y desplazamiento para obra o visitas.";
    }
    if (companyClient) {
      return "Donde opera para proyectos, entregas o visitas comerciales.";
    }
    return "Ámbito geográfico en el que trabaja o entrega.";
  })();

  const materialsLead = (() => {
    if (myPerson) {
      if (kind === "jobboard_worker") {
        return "Listados de materiales públicos vinculados a tu cuenta.";
      }
      if (companyClient) {
        return "Catálogo público de materiales asociado a tu empresa.";
      }
      return "Catálogo público de materiales asociado a tu cuenta.";
    }
    if (kind === "jobboard_worker") {
      return "Precios y fichas del comercio vinculado; el detalle completo está en Materiales.";
    }
    if (companyClient) {
      return "Referencia del catálogo público de la empresa; en Materiales podés ver ítems y precios.";
    }
    return "Referencia del catálogo público; en Materiales está el listado completo.";
  })();

  const contactLead = (() => {
    if (myPerson) {
      if (kind === "jobboard_worker") {
        return "Datos para que te contacten por ofertas o mensajes en Edify.";
      }
      if (companyClient) {
        return "Correo, teléfono y redes que mostrás como empresa.";
      }
      return "Correo, teléfono y redes que mostrás en tu perfil público.";
    }
    if (kind === "jobboard_worker") {
      return "Medios para propuestas o consultas laborales; usalos con buena fe.";
    }
    if (companyClient) {
      return "Canales habituales de la empresa para consultas comerciales.";
    }
    return "Formas de escribirle o seguir su actividad en la red.";
  })();

  const interestsLead = (() => {
    if (myPerson) {
      if (kind === "jobboard_worker") {
        return "Rubros o áreas de tu interés para oportunidades en la red.";
      }
      if (companyClient) {
        return "Rubros o áreas con los que la empresa quiere generar vínculos en Edify.";
      }
      return "Rubros o áreas con las que querés generar vínculos en Edify.";
    }
    if (kind === "jobboard_worker") {
      return "Rubros con los que busca conectar para trabajo u oportunidades.";
    }
    if (companyClient) {
      return "Áreas donde la empresa quiere ampliar red, socios o proyectos.";
    }
    return "Áreas donde busca vínculos con otros profesionales o empresas.";
  })();

  const workerLaborLead = (() => {
    if (kind !== "jobboard_worker") return "";
    if (myPerson) {
      return "Lo que declarás como trabajador/a para bolsa de trabajo y contactos.";
    }
    return "Información declarada para contratación y bolsa de trabajo.";
  })();

  return {
    descriptionTitle,
    descriptionLead,
    skillsLead,
    zonesLead,
    materialsLead,
    contactLead,
    interestsLead,
    workerLaborLead,
  };
}

interface ResumenProps {
  myPerson?: boolean;
  userData?: ProfileData | null;
  profileVisualizerData?: ProfileData | null;
  perfilAjeno?: string | number;
  setInfoReady: (ready: boolean) => void;
}

const Resumen: React.FC<ResumenProps> = ({ myPerson, userData, profileVisualizerData, perfilAjeno, setInfoReady }) => {
  const [isDataReady, setIsDataReady] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setIsDataReady(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const dataSource = (myPerson ? userData : profileVisualizerData) ?? {};
    const isCorrectUser = myPerson ? true : (profileVisualizerData?.id === perfilAjeno || !perfilAjeno);
    const hasData = dataSource && dataSource.username && isCorrectUser;

    if (hasData) {
      timeoutRef.current = setTimeout(() => {
        setIsDataReady(true);
        setInfoReady(true);
      }, MIN_LOADING_TIME);
    } else {
      timeoutRef.current = setTimeout(() => {
        setIsDataReady(true);
        setInfoReady(true);
      }, MAX_LOADING_TIME);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [myPerson, userData, profileVisualizerData, perfilAjeno, setInfoReady]);

  const displayData = useMemo(
    () => (myPerson ? userData : profileVisualizerData) ?? {},
    [myPerson, userData, profileVisualizerData]
  );
  const {
    linkedin,
    instagram,
    facebook,
    twitter,
    phone,
    description,
    jobBoardZones,
    username,
    categoryConnect,
  } = displayData;

  const accountKind = useMemo(() => getResumenAccountKind(displayData), [displayData]);
  const mc = useMemo(() => resumenMicrocopy(accountKind, Boolean(myPerson)), [accountKind, myPerson]);

  const publicCv = useMemo(() => {
    const cv = parseUserCvFromProfile(displayData as Record<string, unknown>);
    if (!cv.visibleOnProfile || !cv.url) return null;
    return cv;
  }, [displayData]);

  const workerLaborRows = useMemo(() => {
    if (accountKind !== "jobboard_worker") return [] as { label: string; value: string }[];
    const d = displayData as Record<string, unknown>;
    const currentJob = d.current_job ?? d.currentJob;
    const unemploymentFund = d.unemployment_fund ?? d.unemploymentFund;
    const endDate = formatProfileFieldDate(
      d.employment_end_date ?? d.employmentEndDate
    );
    const updatedAt = formatProfileFieldDate(d.updated_at ?? d.updatedAt);
    const createdAt = formatProfileFieldDate(d.created_at ?? d.createdAt);
    const rows: { label: string; value: string }[] = [
      { label: "Empleo actual", value: formatOptionalYesNo(currentJob) },
      { label: "Fondo de desempleo", value: formatOptionalYesNo(unemploymentFund) },
    ];
    if (endDate) {
      rows.push({ label: "Fin de contrato", value: endDate });
    }
    if (updatedAt) {
      rows.push({ label: "Última actualización", value: updatedAt });
    }
    if (createdAt) {
      rows.push({ label: "Registrado", value: createdAt });
    }
    return rows;
  }, [accountKind, displayData]);

  const ownerCommerces = useMemo(
    () => (myPerson ? normalizeUserCommerces(userData ?? {}) : []),
    [myPerson, userData]
  );
  const gestionMiComercioHref =
    myPerson && userData?.id != null
      ? buildProfileUrl(String(userData.id), getUserDisplayName(userData), "Gestión", {
          section: "comercio",
        })
      : "";

  const visitorCommerceList = useMemo(
    () => (!myPerson ? normalizeUserCommerces(displayData) : []),
    [myPerson, displayData]
  );

  const commerceIdsForCatalog = useMemo(() => {
    const list = myPerson ? ownerCommerces : visitorCommerceList;
    return list.map((c) => c.id).filter((id) => Number.isFinite(id));
  }, [myPerson, ownerCommerces, visitorCommerceList]);

  const commerceIdsKey = commerceIdsForCatalog.join(",");

  const [catalogInsight, setCatalogInsight] = useState<CatalogInsightState>({
    status: "idle",
    itemTotal: null,
    lastShopUpdateLabel: null,
  });

  useEffect(() => {
    if (commerceIdsForCatalog.length === 0) {
      setCatalogInsight({ status: "idle", itemTotal: null, lastShopUpdateLabel: null });
      return;
    }
    let cancelled = false;
    setCatalogInsight({ status: "loading", itemTotal: null, lastShopUpdateLabel: null });
    (async () => {
      try {
        const catalogSettled = await Promise.allSettled(
          commerceIdsForCatalog.map((id) => getCommerceCatalogMaterials(id, { page: 1, pageSize: 1 }))
        );
        const detailSettled = await Promise.allSettled(
          commerceIdsForCatalog.map((id) => getCommerce(id))
        );
        if (cancelled) return;
        let itemTotal = 0;
        let catalogHits = 0;
        for (const r of catalogSettled) {
          if (r.status !== "fulfilled") continue;
          catalogHits += 1;
          const meta = unwrapCommerceCatalogMaterialsPage(r.value.data);
          if (meta) itemTotal += meta.total;
        }
        let maxMs: number | null = null;
        for (const r of detailSettled) {
          if (r.status !== "fulfilled") continue;
          const ms = parseCommerceDetailUpdatedMs(r.value.data);
          if (ms != null && (maxMs == null || ms > maxMs)) maxMs = ms;
        }
        const lastShopUpdateLabel =
          maxMs != null
            ? new Date(maxMs).toLocaleDateString("es-AR", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })
            : null;
        if (catalogHits === 0) {
          setCatalogInsight({ status: "error", itemTotal: null, lastShopUpdateLabel: null });
          return;
        }
        setCatalogInsight({ status: "ready", itemTotal, lastShopUpdateLabel });
      } catch {
        if (!cancelled) {
          setCatalogInsight({ status: "error", itemTotal: null, lastShopUpdateLabel: null });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [commerceIdsKey]);

  const categoryConnectText = useMemo(() => {
    const categories = Array.isArray(categoryConnect) ? categoryConnect.map((c) => c?.name).filter(Boolean) : [];
    if (categories.length === 0) return null;
    const list = categories.length === 1
      ? categories[0]
      : categories.length === 2
        ? `${categories[0]} y ${categories[1]}`
        : `${categories.slice(0, -1).join(", ")} y ${categories[categories.length - 1]}`;
    const listPhrase = categories.length > 1 ? `áreas como ${list}` : list;
    if (myPerson) {
      return `Te interesa conectar con profesionales de ${listPhrase}.`;
    }
    if (accountKind === "jobboard_worker") {
      return `Está interesado/a en conectar con profesionales de ${listPhrase}.`;
    }
    if (accountKind === "client_company" || accountKind === "other_company") {
      return `La empresa está interesada en conectar con profesionales de ${listPhrase}.`;
    }
    return `Le interesa conectar con profesionales de ${listPhrase}.`;
  }, [categoryConnect, myPerson, accountKind]);

  const zoneList = Array.isArray(jobBoardZones) ? jobBoardZones : [];
  const hasNationalCoverage = zoneList.some((zone) => zone?.allCountry);

  return (
    <>
      {!isDataReady ? (
        <div className={styles.loadingContainer}>
          <RegisteredUserProfileLoader smallVersion={false} noBackground={true} />
        </div>
      ) : (
        <div className={styles.resumen}>
          <div className={styles.resumen_primary}>
            <div className={styles.resumen_layer}>
              <p className={styles.resumen_layerLabel}>Perfil</p>
              <div className={styles.resumen_section}>
                <div className={styles.resumen_section_descriptionIntro}>
                  <h4 className={styles.resumen_section_title}>
                    {mc.descriptionTitle}
                  </h4>
                  <p className={styles.resumen_section_lead}>{mc.descriptionLead}</p>
                </div>
                <p className={styles.resumen_section_text}>
                  {description === null || description === "" ? "No especificado" : (description ?? "No especificado")}
                </p>
              </div>
              <div className={styles.resumen_offerGroup}>
                <p className={styles.resumen_layerLabel}>Oferta y alcance</p>
                <div className={styles.resumen_offerSection}>
                  <h4 className={`${styles.resumen_section_title} ${styles.resumen_section_titleSecondary}`}>
                    Especialidades
                  </h4>
                  <p className={styles.resumen_section_lead}>{mc.skillsLead}</p>
                  <MisSkills perfilAjeno={perfilAjeno} />
                </div>
                {accountKind === "jobboard_worker" ? (
                  <>
                    <div className={styles.resumen_offerSplit} />
                    <div className={styles.resumen_offerSection}>
                      <h4 className={`${styles.resumen_section_title} ${styles.resumen_section_titleSecondary}`}>
                        Situación laboral
                      </h4>
                      <p className={styles.resumen_section_lead}>{mc.workerLaborLead}</p>
                      <ul className={styles.commerceMetaList}>
                        {workerLaborRows.map((row) => (
                          <li key={row.label} className={styles.commerceMetaRow}>
                            <span className={styles.commerceMetaLabel}>{row.label}</span>
                            <span className={styles.commerceMetaValue}>{row.value}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                ) : null}
                <div className={styles.resumen_offerSplit} />
                <div className={styles.resumen_offerSection}>
                  <h4 className={`${styles.resumen_section_title} ${styles.resumen_section_titleSecondary}`}>
                    Cobertura geográfica
                  </h4>
                  <p className={styles.resumen_section_lead}>
                    {mc.zonesLead}
                  </p>
                  <div className={styles.zoneChips}>
                    {hasNationalCoverage ? (
                      <div className={styles.zoneChipNational}>
                        <Globe size={16} strokeWidth={2} className={styles.zoneChipIcon} />
                        <span className={styles.zoneChipNationalText}>
                          <strong>Todo el país</strong>
                          <span className={styles.zoneChipMuted}> · Cobertura nacional</span>
                        </span>
                      </div>
                    ) : zoneList.length > 0 ? (
                      zoneList.map((zone, index) => (
                        <div key={index} className={styles.zoneChip}>
                          <MapPin size={16} strokeWidth={2} className={styles.zoneChipIcon} />
                          <span className={styles.zoneChipLabel}>
                            {zone?.jurisdiccion?.name}
                            {zone?.city?.trim()
                              ? (
                                <>
                                  <span className={styles.zoneChipSep}>·</span>
                                  <span className={styles.zoneChipCity}>{zone.city.split(",")[0]?.trim()}</span>
                                </>
                              )
                              : null}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className={styles.zoneEmpty}>No especificado</p>
                    )}
                  </div>
                </div>
                {(myPerson ||
                  (!myPerson &&
                    visitorCommerceList.length > 0 &&
                    (displayData.id != null || perfilAjeno != null))) && (
                  <>
                    <div className={styles.resumen_offerSplit} />
                    <div className={styles.resumen_offerSection}>
                      <div className={styles.resumen_section_headerCommerce}>
                        <h4 className={`${styles.resumen_section_title} ${styles.resumen_section_titleSecondary}`}>
                          Listados de materiales
                        </h4>
                        {myPerson && gestionMiComercioHref ? (
                          <Link className={styles.commerceHeaderLink} to={gestionMiComercioHref}>
                            Administrar
                          </Link>
                        ) : !myPerson ? (
                          <Link
                            className={styles.commerceHeaderLink}
                            to={buildProfileUrl(
                              String(displayData.id ?? perfilAjeno),
                              getUserDisplayName(displayData),
                              "Materiales"
                            )}
                          >
                            Ver listado
                          </Link>
                        ) : null}
                      </div>
                      <p className={styles.resumen_section_lead}>
                        {mc.materialsLead}
                      </p>
                      {myPerson ? (
                        <>
                          {ownerCommerces.length > 0 ? (
                            <p className={styles.commerceSummaryName}>
                              {ownerCommerces.length === 1
                                ? ownerCommerces[0]?.name?.trim() || "Comercio registrado"
                                : ownerCommerces.map((c) => c.name).join(" · ")}
                            </p>
                          ) : null}
                          {ownerCommerces.length === 0 ? (
                            <p className={styles.resumen_section_text}>Sin comercio dado de alta.</p>
                          ) : null}
                        </>
                      ) : (
                        <>
                          {visitorCommerceList.length > 1 ? (
                            <p className={styles.commerceSummaryName}>
                              {visitorCommerceList.map((c) => c.name).join(" · ")}
                            </p>
                          ) : visitorCommerceList.length === 1 && visitorCommerceList[0]?.name?.trim() ? (
                            <p className={styles.commerceSummaryName}>{visitorCommerceList[0].name.trim()}</p>
                          ) : null}
                        </>
                      )}
                      {commerceIdsForCatalog.length > 0 ? (
                        <>
                          {catalogInsight.status === "loading" ? (
                            <p className={styles.commerceMetaMuted}>Consultando el catálogo…</p>
                          ) : null}
                          {catalogInsight.status === "ready" && catalogInsight.itemTotal !== null ? (
                            <ul className={styles.commerceMetaList}>
                              <li className={styles.commerceMetaRow}>
                                <span className={styles.commerceMetaLabel}>Ítems publicados</span>
                                <span className={styles.commerceMetaValue}>{catalogInsight.itemTotal}</span>
                              </li>
                              {catalogInsight.lastShopUpdateLabel ? (
                                <li className={styles.commerceMetaRow}>
                                  <span className={styles.commerceMetaLabel}>Última actualización</span>
                                  <span className={styles.commerceMetaValue}>{catalogInsight.lastShopUpdateLabel}</span>
                                </li>
                              ) : null}
                            </ul>
                          ) : null}
                          {catalogInsight.status === "error" ? (
                            <p className={styles.resumen_section_text}>
                              {myPerson
                                ? "No pudimos leer el estado del catálogo. Podés revisarlo desde Gestión."
                                : "Podés ver más detalle en la pestaña Materiales."}
                            </p>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className={styles.resumen_aside}>
            <div className={styles.resumen_layer}>
              <p className={styles.resumen_layerLabel}>Canal de contacto</p>
              <div className={`${styles.resumen_section} ${styles.resumen_section_aside}`}>
                <h4 className={`${styles.resumen_section_title} ${styles.resumen_section_titleSecondary}`}>
                  Datos de contacto
                </h4>
                <p className={styles.resumen_section_lead}>{mc.contactLead}</p>
                <div className={styles.resumen_section_info}>
                  <div className={styles.resumen_section_info_item}>
                    <Mail size={20} className={styles.resumen_section_info_item_icon} />
                    <p className={styles.resumen_section_info_item_data}>{username ?? ""}</p>
                  </div>
                  {phone && (
                    <div className={styles.resumen_section_info_item}>
                      <Phone size={20} className={styles.resumen_section_info_item_icon} />
                      <p className={styles.resumen_section_info_item_data}>{phone}</p>
                    </div>
                  )}
                  {instagram && (
                    <div className={styles.resumen_section_info_item}>
                      <Instagram size={20} className={styles.resumen_section_info_item_icon} />
                      <p className={styles.resumen_section_info_item_data}>{instagram}</p>
                    </div>
                  )}
                  {linkedin && (
                    <div className={styles.resumen_section_info_item}>
                      <Linkedin size={20} className={styles.resumen_section_info_item_icon} />
                      <p className={styles.resumen_section_info_item_data}>{linkedin}</p>
                    </div>
                  )}
                  {facebook && (
                    <div className={styles.resumen_section_info_item}>
                      <Facebook size={20} className={styles.resumen_section_info_item_icon} />
                      <p className={styles.resumen_section_info_item_data}>{facebook}</p>
                    </div>
                  )}
                  {twitter && (
                    <div className={styles.resumen_section_info_item}>
                      <Twitter size={20} className={styles.resumen_section_info_item_icon} />
                      <p className={styles.resumen_section_info_item_data}>{twitter}</p>
                    </div>
                  )}
                  {publicCv?.url ? (
                    <div
                      className={`${styles.resumen_section_info_item} ${styles.resumen_section_info_item_cv}`}
                    >
                      <FileText size={20} className={styles.resumen_section_info_item_icon} />
                      <div className={styles.resumen_cvContent}>
                        <span className={styles.resumen_cvLabel}>Currículum vitae</span>
                        <a
                          href={publicCv.url}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.resumen_cvLink}
                          aria-label="Ver o descargar currículum vitae"
                        >
                          Ver o descargar
                          <ExternalLink size={14} aria-hidden />
                        </a>
                        {formatUserCvUpdatedAt(publicCv.updatedAt) ? (
                          <p className={styles.resumen_cvMeta}>
                            Actualizado el {formatUserCvUpdatedAt(publicCv.updatedAt)}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              {categoryConnectText && (
                <div className={`${styles.resumen_section} ${styles.resumen_section_aside}`}>
                  <h4 className={`${styles.resumen_section_title} ${styles.resumen_section_titleSecondary}`}>
                    Intereses en la red
                  </h4>
                  <p className={styles.resumen_section_lead}>{mc.interestsLead}</p>
                  <p className={styles.resumen_section_text}>{categoryConnectText}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

};

export default Resumen;
